import { useCallback, useState } from "react";

import { Color } from "@vericus/cadmus-ui";

import dayjs from "dayjs";

import { getLocalDate } from "@/utils/datetime/transform-date";

/**
 * Colors of dates. All date color styling should take from `dateColors`
 */
export const dateColors = {
  dueDate: "lilac500" as const,
  returnDate: "teal500" as const,
  draftDueDate: "pink500" as const,
  extension: "redA500" as const,
  scheduledDate: "blue500" as const,
  today: "navy700" as const,
};

/**
 * Create a callback to re-render the Datepicker to the current month.
 *
 * Returns a pair of `[todayKey, callback]`, where the 'todayKey' value should
 * be provided to the `<DatePicker />` component which needs to jump. The jump
 * is triggered using the 'callback'.
 *
 * Since the `react-dates` DatePicker provides no such prop to hook into, we
 * trick it to re-render itself which automatically sets it to the current
 * month. The re-renders can be triggered by providing a `key` prop to it, which
 * is updated when calling the returned `jumpToToday` callback.
 */
export function useJumpToToday(): [number, () => void] {
  const [key, setKey] = useState(0);
  const jumpToToday = useCallback(() => {
    setKey((k) => k + 1);
  }, [setKey]);
  return [key, jumpToToday];
}

export type DayHighlight = Record<string, Color[]>;
/**
 * Properties to customise the highlight for a single date.
 * key is the date in ISO8601 (e.g. '2020-04-17' ) and value is color highlight
 */
export const dayHighlightKey = (d: Date) => dayjs(d).format("YYYY-MM-DD");

/**
 * Get the dayHighlight for defined dates and today
 */
export function getHighlightDays(dates: {
  dueDate: string | null;
  returnDate: string | null;
  draftDueDate?: string;
  extensionDate?: string;
  scheduledDate?: string;
}): DayHighlight {
  const dayHighlight: DayHighlight = {};

  // dueDate
  if (dates.dueDate) {
    const key = dayHighlightKey(getLocalDate(dates.dueDate));
    dayHighlight[key] = [...(dayHighlight[key] || []), dateColors.dueDate];
  }

  // returnDate
  if (dates.returnDate) {
    const key = dayHighlightKey(getLocalDate(dates.returnDate));
    dayHighlight[key] = [...(dayHighlight[key] || []), dateColors.returnDate];
  }

  // draftDueDate
  if (dates.draftDueDate) {
    const key = dayHighlightKey(getLocalDate(dates.draftDueDate));
    dayHighlight[key] = [...(dayHighlight[key] || []), dateColors.draftDueDate];
  }

  // extensionDate
  if (dates.extensionDate) {
    const key = dayHighlightKey(getLocalDate(dates.extensionDate));
    dayHighlight[key] = [...(dayHighlight[key] || []), dateColors.extension];
  }

  // scheduledDate
  if (dates.scheduledDate) {
    const key = dayHighlightKey(getLocalDate(dates.scheduledDate));
    dayHighlight[key] = [
      ...(dayHighlight[key] || []),
      dateColors.scheduledDate,
    ];
  }

  // today
  const key = dayHighlightKey(new Date());
  dayHighlight[key] = [...(dayHighlight[key] ?? []), dateColors.today];

  return dayHighlight;
}

/**
 * Checks whether date `d` is invalid or not.
 *
 * date is invalid if
 *    1. before (inclusive to minutes) any of the dates in `before`
 *    2. after (inclusive to minutes) any of the dates in `after`
 *    `onBeforeInvalid` and `onAfterInvalid` will also be called respectively.
 */
export const isDateInvalid = (
  d: Date,
  before: Date[],
  after: Date[],
  onBeforeInvalid?: (b: Date) => void,
  onAfterInvalid?: (a: Date) => void
): boolean => {
  for (let i = 0; i < before.length; i++) {
    const b = before[i]!;
    if (dayjs(d).isSame(b, "minute") || dayjs(d).isBefore(b)) {
      onBeforeInvalid?.(b);
      return true;
    }
  }

  for (let i = 0; i < after.length; i++) {
    const a = after[i]!;
    if (dayjs(d).isSame(a, "minute") || dayjs(d).isAfter(a)) {
      onAfterInvalid?.(a);
      return true;
    }
  }

  return false;
};
